import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Square, Loader2, RefreshCw, Clock, CheckCircle2, Factory, AlertCircle, ChevronRight, Search, X,
} from 'lucide-react';
import { api, getProductionPlan, startShift as apiStartShift } from '@/utils/api';
import { PlanOrderCard, type PlanOrder } from '@/components/manufacturing/PlanOrderCard';

import { ShiftBoardPanel } from './ShiftBoardPanel';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShiftRecord {
  id: string;
  date: string;
  status: 'active' | 'completed' | 'not_started' | string;
  startTime: string | null;
  endTime: string | null;
  productionOrderIds?: string[];
}

// Extended PlanOrder with trigger source so the picker can filter by it.
// The base PlanOrder type doesn't carry this, so we widen it locally.
export interface PickerOrder extends PlanOrder {
  triggerSource?: string | null;
}

export type PickerFilter = 'all' | 'sales_order' | 'stock_order' | 'production_plan' | 'backlog';

const FILTER_CHIPS: { key: PickerFilter; label: string; hint: string }[] = [
  { key: 'all',             label: 'All',             hint: 'Every candidate order' },
  { key: 'sales_order',     label: 'SO-driven',       hint: 'Customer-triggered' },
  { key: 'stock_order',     label: 'Stock plan',      hint: 'Routine stock-in/out' },
  { key: 'production_plan', label: 'Production plan', hint: 'Planner-scheduled' },
  { key: 'backlog',         label: 'Backlog',         hint: 'No planned start' },
];

function normalise(o: any): PickerOrder {
  return {
    id: o.id,
    orderNumber: o.orderNumber || o.number || `PO-${String(o.id ?? '').slice(-6).toUpperCase()}`,
    productName: o.product?.name || o.productName || 'Unknown product',
    targetQty: Number(o.targetQty) || Number(o.quantity) || 0,
    manpowerCount: o.manpowerCount ?? null,
    shiftTarget: o.shiftTarget ?? null,
    status: o.status || 'pending',
    priority: o.priority ?? null,
    plannedStart: o.plannedStart ?? null,
    materialAvailable: typeof o.materialAvailable === 'boolean' ? o.materialAvailable : null,
    triggerSource: o.triggerSource ?? null,
  };
}

function isToday(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
}

function isDefaultSelected(o: PickerOrder): boolean {
  const statusOk = ['planned', 'ready'].includes(String(o.status || '').toLowerCase());
  return Boolean(o.plannedStart) && isToday(o.plannedStart) && statusOk;
}

function dedupeById<T extends { id: string }>(arr: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const x of arr) {
    if (x?.id && !seen.has(x.id)) {
      seen.add(x.id);
      out.push(x);
    }
  }
  return out;
}

function showToast(msg: string, type: 'success' | 'error' = 'success') {
  const el = document.createElement('div');
  el.className = `fixed bottom-4 right-4 z-[9999] rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
    type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
  }`;
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 300);
  }, 3000);
}

// ── Start Shift Modal (exported, reused by BucketedProductionPlanPage) ────────
//
// Norman-grade affordance: default-checked = today's planned orders (planner's
// intent), unchecked-but-selectable = every OTHER candidate the floor manager
// might pull in (backlog, future-planned, SO-driven, stock routine). Filter
// chips + search let the foreman slice the list without losing the
// default-selection state. Header chip always shows "X of Y · N default · M
// added" so the manager knows exactly what deviates from plan.
//
// Back-compat: callers can keep passing `todaysOrders` + `preselectedIds`. When
// `candidateOrders` is omitted, it falls back to `todaysOrders`.

export function StartShiftModal({
  open,
  onClose,
  todaysOrders,
  candidateOrders,
  preselectedIds,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  todaysOrders: PickerOrder[];
  candidateOrders?: PickerOrder[]; // broader pool — backlog + future + other trigger sources
  preselectedIds?: string[];
  onStarted?: (shift: ShiftRecord | null, selectedIds: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState<PickerFilter>('all');
  const [query, setQuery] = useState('');

  // Merge + dedupe candidates (todaysOrders always included so default-checked
  // logic works even if the caller forgets to add them to candidateOrders).
  const orders = useMemo<PickerOrder[]>(() => {
    const merged = [...(candidateOrders ?? []), ...(todaysOrders ?? [])];
    return dedupeById(merged);
  }, [candidateOrders, todaysOrders]);

  // Default-selected IDs = planned/ready AND plannedStart within today.
  const defaultIds = useMemo<Set<string>>(() => {
    return new Set(orders.filter(isDefaultSelected).map((o) => o.id));
  }, [orders]);

  // Reset selection state each time the modal opens.
  useEffect(() => {
    if (open) {
      // Explicit preselectedIds wins (caller-forced). Otherwise use default set.
      const initial =
        preselectedIds && preselectedIds.length > 0
          ? new Set(preselectedIds)
          : new Set(defaultIds);
      setSelected(initial);
      setFilter('all');
      setQuery('');
    }
  }, [open, preselectedIds, defaultIds]);

  // Apply filter chip + search.
  const visibleOrders = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      // Filter chip
      if (filter === 'backlog') {
        if (o.plannedStart) return false;
      } else if (filter !== 'all') {
        const src = (o.triggerSource ?? 'production_plan').toLowerCase();
        if (src !== filter) return false;
      }
      // Search
      if (q) {
        const hay = `${o.orderNumber} ${o.productName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, filter, query]);

  // Counts for filter-chip badges — count over the FULL candidate pool, not
  // the already-filtered view (so chips show stable totals).
  const chipCounts = useMemo(() => {
    const c: Record<PickerFilter, number> = {
      all: orders.length,
      sales_order: 0,
      stock_order: 0,
      production_plan: 0,
      backlog: 0,
    };
    for (const o of orders) {
      if (!o.plannedStart) c.backlog += 1;
      const src = (o.triggerSource ?? 'production_plan').toLowerCase();
      if (src === 'sales_order') c.sales_order += 1;
      else if (src === 'stock_order') c.stock_order += 1;
      else c.production_plan += 1;
    }
    return c;
  }, [orders]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const o of visibleOrders) next.add(o.id);
      return next;
    });
  };
  const clearAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const o of visibleOrders) next.delete(o.id);
      return next;
    });
  };
  const resetToDefault = () => setSelected(new Set(defaultIds));

  // Header-chip breakdown.
  const selectedCount = selected.size;
  const defaultInSelection = useMemo(() => {
    let n = 0;
    for (const id of selected) if (defaultIds.has(id)) n += 1;
    return n;
  }, [selected, defaultIds]);
  const addedCount = selectedCount - defaultInSelection;
  const missedDefaults = defaultIds.size - defaultInSelection; // default orders the manager deselected

  const onConfirm = async () => {
    if (selected.size === 0) {
      showToast('Pick at least one order for this shift', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const ids = Array.from(selected);
      const shift = await apiStartShift({ productionOrderIds: ids });
      showToast(`Shift started with ${ids.length} orders`);
      onStarted?.(shift ?? null, ids);
      onClose();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to start shift', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        {/* Title */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-indigo-50 px-5 py-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Play className="h-5 w-5 text-indigo-600" /> Start shift — pick orders
            </h2>
            <p className="mt-0.5 text-xs text-gray-600">
              Today's plan is pre-selected. Add backlog, SO-driven, or stock-routine orders as needed.
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-white" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filter chips + search */}
        <div className="border-b border-gray-200 bg-white px-5 pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {FILTER_CHIPS.map((c) => {
              const active = filter === c.key;
              const n = chipCounts[c.key];
              return (
                <button
                  key={c.key}
                  onClick={() => setFilter(c.key)}
                  title={c.hint}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition ${
                    active
                      ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
                  }`}
                >
                  {c.label}
                  <span
                    className={`rounded-full px-1.5 text-[10px] ${
                      active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {n}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="my-3 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5">
            <Search className="h-3.5 w-3.5 text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by order number or product name…"
              className="w-full bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="rounded p-0.5 text-gray-400 hover:bg-white hover:text-gray-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Header chip + quick actions */}
          <div className="flex flex-wrap items-center justify-between gap-2 pb-3 text-xs">
            <div className="inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-medium text-indigo-800">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>
                {selectedCount} of {orders.length} selected
              </span>
              <span className="text-indigo-400">·</span>
              <span>{defaultInSelection} default</span>
              <span className="text-indigo-400">·</span>
              <span>{addedCount} added</span>
              {missedDefaults > 0 && (
                <>
                  <span className="text-indigo-400">·</span>
                  <span className="text-orange-700">{missedDefaults} default unselected</span>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={resetToDefault} className="text-indigo-600 hover:text-indigo-700">
                Reset to today's plan
              </button>
              <span className="text-gray-300">|</span>
              <button onClick={selectAllVisible} className="text-indigo-600 hover:text-indigo-700">
                Select visible
              </button>
              <button onClick={clearAllVisible} className="text-gray-500 hover:text-gray-700">
                Clear visible
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable list */}
        <div className="flex-1 overflow-y-auto p-4">
          {orders.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
              No production orders available. Schedule orders from the{' '}
              <Link to="/manufacturing/planning" className="text-indigo-600 hover:underline">
                planning console
              </Link>
              .
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
              No orders match this filter or search.
            </div>
          ) : (
            <div className="space-y-2">
              {visibleOrders.map((o) => {
                const isSel = selected.has(o.id);
                const isDefault = defaultIds.has(o.id);
                const src = (o.triggerSource ?? 'production_plan').toLowerCase();
                return (
                  <label
                    key={o.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition ${
                      isSel ? 'border-indigo-400 bg-indigo-50/60' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      checked={isSel}
                      onChange={() => toggle(o.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono text-xs font-semibold text-gray-900">{o.orderNumber}</span>
                        {isDefault && (
                          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                            Today's plan
                          </span>
                        )}
                        {src === 'sales_order' && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-blue-700">
                            SO
                          </span>
                        )}
                        {src === 'stock_order' && (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700">
                            Stock
                          </span>
                        )}
                        {src === 'production_plan' && !isDefault && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
                            Plan
                          </span>
                        )}
                        {!o.plannedStart && (
                          <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-700">
                            Backlog
                          </span>
                        )}
                        {o.priority && (
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
                            {o.priority}
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 truncate text-sm text-gray-700">{o.productName}</div>
                      <div className="text-xs text-gray-500">
                        Target {o.targetQty}
                        {o.manpowerCount ? ` · Manpower ${o.manpowerCount}` : ''}
                        {o.shiftTarget ? ` · Shift target ${o.shiftTarget}` : ''}
                        {o.plannedStart ? ` · Planned ${new Date(o.plannedStart).toLocaleDateString('en-IN')}` : ''}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting || selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Confirm & start ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ShiftControlPage() {
  const [boardKey, setBoardKey] = useState(0);
  const [todaysOrders, setTodaysOrders] = useState<PickerOrder[]>([]);
  const [candidateOrders, setCandidateOrders] = useState<PickerOrder[]>([]);
  const [shift, setShift] = useState<ShiftRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    setBoardKey((k) => k + 1);
    try {
      setLoading(true);
      setError(null);
      // Fetch in parallel:
      //   (1) planning — gives us today/tomorrow/thisWeek/backlog buckets
      //   (2) orders?status=planned,ready — full list of actionable orders
      //   (3) current shift
      const [planRes, ordersRes, shiftRes] = await Promise.all([
        getProductionPlan().catch(() => null),
        api
          .get('/manufacturing-flow/orders', { params: { status: 'planned,ready', limit: 500 } })
          .then((r) => r.data?.data ?? r.data)
          .catch(() => null),
        api.get('/manufacturing-p0/shift/current').catch(() => null),
      ]);

      // --- Resolve today's orders (the pre-checked default set) ---
      let today: PickerOrder[] = [];
      if (planRes && (planRes.today || planRes.tomorrow)) {
        today = (planRes.today ?? []).map(normalise);
      } else if (planRes?.orders) {
        today = planRes.orders
          .map(normalise)
          .filter((o: PickerOrder) => isToday(o.plannedStart));
      }
      setTodaysOrders(today);

      // --- Resolve broader candidate pool for the picker ---
      // Union of: planning buckets (today/tomorrow/thisWeek/backlog) + /orders list
      const fromPlan: PickerOrder[] = [];
      if (planRes) {
        for (const k of ['today', 'tomorrow', 'thisWeek', 'backlog'] as const) {
          const arr = planRes[k];
          if (Array.isArray(arr)) fromPlan.push(...arr.map(normalise));
        }
        if (fromPlan.length === 0 && Array.isArray(planRes.orders)) {
          fromPlan.push(...planRes.orders.map(normalise));
        }
      }

      const fromList: PickerOrder[] = Array.isArray(ordersRes)
        ? ordersRes.map(normalise)
        : Array.isArray(ordersRes?.orders)
        ? ordersRes.orders.map(normalise)
        : [];

      const pool = dedupeById([...today, ...fromPlan, ...fromList]).filter((o) => {
        const s = String(o.status || '').toLowerCase();
        // Only orders that could reasonably be run in a shift
        return ['planned', 'ready', 'material_check', 'in_progress'].includes(s);
      });
      setCandidateOrders(pool);

      const s = shiftRes?.data?.data ?? null;
      setShift(s);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load shift data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStop = async () => {
    if (!shift || shift.status !== 'active') return;
    setStopping(true);
    try {
      await api.post('/manufacturing-p0/shift/stop', {});
      showToast('Shift stopped');
      load();
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Failed to stop shift', 'error');
    } finally {
      setStopping(false);
    }
  };

  const shiftActive = shift?.status === 'active';

  const startTimeFmt = useMemo(() => {
    if (!shift?.startTime) return null;
    try {
      return new Date(shift.startTime).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return shift.startTime;
    }
  }, [shift?.startTime]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
            <Factory className="h-6 w-6 text-indigo-600" />
            Shift control
          </h1>
          <p className="mt-1 text-sm text-gray-600">Start and stop the shop-floor shift.</p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Shift state card */}
      <div
        className={`mb-6 rounded-xl border p-6 shadow-sm ${
          shiftActive
            ? 'border-emerald-200 bg-emerald-50'
            : shift?.status === 'completed'
            ? 'border-gray-200 bg-gray-50'
            : 'border-indigo-200 bg-indigo-50'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              {shiftActive
                ? `Shift active${startTimeFmt ? ` since ${startTimeFmt}` : ''}`
                : shift?.status === 'completed'
                ? 'Last shift completed'
                : 'No active shift'}
            </div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">
              {shiftActive ? 'Running' : shift?.status === 'completed' ? 'Completed' : 'Not started'}
            </div>
            <div className="mt-1 text-xs text-gray-500">
              {todaysOrders.length} orders planned for today
            </div>
          </div>

          <div className="flex items-center gap-2">
            {shiftActive ? (
              <button
                onClick={handleStop}
                disabled={stopping}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-red-700 disabled:bg-gray-300"
              >
                {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
                Stop shift
              </button>
            ) : (
              <button
                onClick={() => setModalOpen(true)}
                disabled={candidateOrders.length === 0 && todaysOrders.length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <Play className="h-4 w-4" /> Start shift
              </button>
            )}
          </div>
        </div>

        {shiftActive && shift?.productionOrderIds && shift.productionOrderIds.length > 0 && (
          <div className="mt-4 border-t border-emerald-200 pt-3 text-xs text-emerald-800">
            <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
            {shift.productionOrderIds.length} orders attached to this shift
          </div>
        )}
      </div>

      <ShiftBoardPanel refreshKey={boardKey} />

      {/* Today's orders preview */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Today's planned orders</h2>
        <Link
          to="/production/plan"
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          Open plan <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {todaysOrders.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-600">
          No orders planned for today.{' '}
          <Link to="/manufacturing/planning" className="text-indigo-600 hover:underline">
            Plan orders
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {todaysOrders.slice(0, 10).map((o) => (
            <PlanOrderCard key={o.id} order={o} />
          ))}
        </div>
      )}

      <StartShiftModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        todaysOrders={todaysOrders}
        candidateOrders={candidateOrders}
        onStarted={() => {
          setModalOpen(false);
          load();
        }}
      />
    </div>
  );
}
